/**
 * Home Page
 *
 * Purpose: Central landing page for authenticated users with personalized dashboard,
 *          featuring a four-column layout per Figma design with Media Planning,
 *          Analytics activity feed, Recent Items, and Quick Links sections.
 *
 * Architecture: Uses SkeletonPage template with TopBanner (search mode) and purple LeftSidebar.
 *              Implements Orchestrate brand identity colors and typography.
 *
 * Dependencies: React, Material-UI, SkeletonPage, AuthContext, React Router
 * Author: Development Team
 * Created: 2025-01-27
 * Updated: 2025-11-29 - Redesigned per Figma brand identity specs
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Avatar,
  Chip,
  Skeleton,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Divider,
  Link,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Assessment, MoreHoriz, ArrowForward, Chat as ChatIcon } from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { ModuleKey, PermissionKey } from '../../constants/permissionKeys';
import { useNavigate } from 'react-router-dom';
import SkeletonPage from '../../components/layout/SkeletonPage';
import { brandColors } from '../../theme/orchestrateTheme';

// Styled components

// Chat with Maestro Button - Teal to Magenta gradient per Figma design (page 18)
const MaestroCTAButton = styled(Button)(() => ({
  background: `linear-gradient(90deg, ${brandColors.teal} 0%, ${brandColors.deepViolet} 50%, ${brandColors.maestro} 100%)`,
  color: '#ffffff',
  fontWeight: 600,
  padding: '14px 32px',
  borderRadius: 8,
  textTransform: 'none',
  fontSize: '0.95rem',
  boxShadow: '0 4px 12px rgba(85, 18, 100, 0.35)',
  '&:hover': {
    background: `linear-gradient(90deg, ${brandColors.brightTeal} 0%, ${brandColors.purple} 50%, ${brandColors.maestroLight} 100%)`,
    boxShadow: '0 6px 16px rgba(85, 18, 100, 0.45)',
  },
}));

// Media Planning Card - Darker purple background per Figma
const MediaPlanningCard = styled(Card)(() => ({
  backgroundColor: '#3D0E4A', // Darker than deepViolet per Figma design
  borderRadius: 12,
  color: '#ffffff',
  height: '100%',
}));

// White section cards
const SectionCard = styled(Card)(() => ({
  borderRadius: 12,
  boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
  border: `1px solid ${brandColors.paleGray}`,
  height: '100%',
}));

// Quick Action Card - Tall cards on the right, left-justified per brand spec
const QuickActionCard = styled(Card)(() => ({
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  borderRadius: 12,
  border: `1px solid ${brandColors.paleGray}`,
  boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  textAlign: 'left',
  padding: '24px',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
  },
}));

// KPI Box - Dark background metric display
const KPIBox = styled(Box)(() => ({
  backgroundColor: 'rgba(0,0,0,0.2)',
  borderRadius: 8,
  padding: '12px 8px',
  textAlign: 'center',
  flex: 1,
}));

// Activity List Item
const ActivityListItem = styled(ListItem)(() => ({
  padding: '12px 16px',
  '&:hover': {
    backgroundColor: brandColors.offWhite,
  },
}));

// Mock data types
interface RecentItem {
  id: string;
  title: string;
  budget: string;
  dates: string;
  status: 'Draft' | 'Approved' | 'Active';
  timestamp: string;
  brandImage: string;
}

interface ActivityItem {
  id: string;
  brandName: string;
  campaignName: string;
  dates: string;
}

interface CampaignPreview {
  id: string;
  name: string;
  budget: string;
  dates: string;
  status: string;
  score: number;
  kpis: {
    attention: string;
    viewability: string;
    reach: string;
  };
  audience: {
    gender: string;
    age: string;
    hhi: string;
  };
}

const HomePage: React.FC = () => {
  const { state, getCurrentTenant, can, hasModule } = useAuth();
  const { user } = state;
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [recentItems, setRecentItems] = useState<RecentItem[]>([]);
  const [activityItems, setActivityItems] = useState<ActivityItem[]>([]);
  const [campaignPreview, setCampaignPreview] = useState<CampaignPreview | null>(null);

  const currentTenant = getCurrentTenant();

  // Load mock data
  useEffect(() => {
    // Mock recent items
    const mockRecentItems: RecentItem[] = [
      {
        id: '1',
        title: 'Monthly Performance Report',
        budget: 'Budget: 200k',
        dates: 'In Market Dates: Oct 20 - Dec 31',
        status: 'Draft',
        timestamp: '2 hours ago',
        brandImage: '/images/brand-on.png',
      },
      {
        id: '2',
        title: 'Monthly Performance Report',
        budget: 'Budget: 200k',
        dates: 'In Market Dates: Oct 20 - Dec 31',
        status: 'Approved',
        timestamp: '2 hours ago',
        brandImage: '/images/brand-away.png',
      },
      {
        id: '3',
        title: 'Monthly Performance Report',
        budget: 'Budget: 200k',
        dates: 'In Market Dates: Oct 20 - Dec 31',
        status: 'Active',
        timestamp: '2 hours ago',
        brandImage: '/images/brand-g.png',
      },
    ];

    // Mock activity items
    const mockActivityItems: ActivityItem[] = [
      {
        id: '1',
        brandName: 'Brand',
        campaignName: 'Campaign Name',
        dates: 'In Market Dates: Oct 20 - Dec 31',
      },
      {
        id: '2',
        brandName: 'Brand',
        campaignName: 'Campaign Name',
        dates: 'In Market Dates: Oct 20 - Dec 31',
      },
      {
        id: '3',
        brandName: 'Brand',
        campaignName: 'Campaign Name',
        dates: 'In Market Dates: Oct 20 - Dec 31',
      },
      {
        id: '4',
        brandName: 'Brand',
        campaignName: 'Campaign Name',
        dates: 'In Market Dates: Oct 20 - Dec 31',
      },
    ];

    // Mock campaign preview
    const mockCampaign: CampaignPreview = {
      id: '1',
      name: 'Q4 Holiday Campaign',
      budget: 'Budget: 200k',
      dates: 'In Market Dates: Oct 20 - Dec 31',
      status: 'Live',
      score: 9.98,
      kpis: {
        attention: '75%',
        viewability: '123k',
        reach: '200k',
      },
      audience: {
        gender: 'Male/ Female',
        age: '18-34 Years Old',
        hhi: '100K+',
      },
    };

    setTimeout(() => {
      setRecentItems(mockRecentItems);
      setActivityItems(mockActivityItems);
      setCampaignPreview(mockCampaign);
      setLoading(false);
    }, 600);
  }, []);

  const getUserDisplayName = (): string => {
    if (user?.email) {
      return user.email.split('@')[0];
    }
    return 'User';
  };

  const getStatusColor = (status: string): 'default' | 'success' | 'primary' => {
    switch (status) {
      case 'Approved':
        return 'success';
      case 'Active':
        return 'primary';
      default:
        return 'default';
    }
  };

  if (!user) {
    return (
      <SkeletonPage pageTitle="Home" showSearch>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <Typography>Loading...</Typography>
        </Box>
      </SkeletonPage>
    );
  }

  return (
    <SkeletonPage pageTitle="Home" showSearch>
      {/* Welcome Header Section */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 200,
                color: 'text.primary',
                mb: 1,
                fontSize: { xs: '2rem', md: '2.5rem' },
              }}
            >
              Welcome back, {getUserDisplayName()}!
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Here's your Orchestrate HQ AI Home{' '}
              {currentTenant && (
                <Link
                  href="#"
                  sx={{
                    color: brandColors.teal,
                    textDecoration: 'none',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  {currentTenant.name}
                </Link>
              )}
            </Typography>
          </Box>

          {/* Chat with Maestro CTA */}
          {hasModule(ModuleKey.PLANNING_MAESTRO) && (
            <MaestroCTAButton startIcon={<ChatIcon />} onClick={() => navigate('/chat')}>
              CHAT WITH MAESTRO
            </MaestroCTAButton>
          )}
        </Box>
      </Box>

      {/* Four Column Dashboard Layout */}
      <Grid container spacing={3}>
        {/* Column 1: Media Planning - Purple Card */}
        <Grid size={{ xs: 12, md: 3 }}>
          <MediaPlanningCard>
            <CardContent sx={{ p: 0 }}>
              {/* Header */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  p: 2,
                  pb: 1,
                }}
              >
                <Typography variant="h5" sx={{ fontWeight: 600, fontSize: '1.25rem' }}>
                  Media Planning
                </Typography>
                <IconButton size="small" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                  <MoreHoriz />
                </IconButton>
              </Box>

              {loading ? (
                <Box sx={{ p: 2 }}>
                  <Skeleton
                    variant="rectangular"
                    height={200}
                    sx={{ bgcolor: 'rgba(255,255,255,0.1)' }}
                  />
                </Box>
              ) : (
                campaignPreview && (
                  <>
                    {/* Campaign Preview */}
                    <Box sx={{ px: 2, pb: 2 }}>
                      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                        {/* Brand Logo Placeholder */}
                        <Avatar
                          sx={{
                            width: 80,
                            height: 80,
                            bgcolor: '#E91E63',
                            fontSize: '0.8rem',
                          }}
                        >
                          poppi
                        </Avatar>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="subtitle1" fontWeight={600}>
                            {campaignPreview.name}
                          </Typography>
                          <Typography variant="caption" sx={{ opacity: 0.8, display: 'block' }}>
                            {campaignPreview.budget}
                          </Typography>
                          <Typography variant="caption" sx={{ opacity: 0.8, display: 'block' }}>
                            {campaignPreview.dates}
                          </Typography>
                          <Chip
                            label={campaignPreview.status}
                            size="small"
                            sx={{
                              mt: 1,
                              bgcolor: brandColors.brightTeal,
                              color: 'white',
                              fontWeight: 600,
                              fontSize: '0.7rem',
                            }}
                          />
                        </Box>
                      </Box>

                      {/* Score */}
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          mt: 2,
                        }}
                      >
                        <Typography variant="body2" fontWeight={500}>
                          Score
                        </Typography>
                        <Typography variant="h4" fontWeight={700}>
                          {campaignPreview.score}
                        </Typography>
                      </Box>
                    </Box>

                    {/* KPIs Section */}
                    <Box sx={{ px: 2, pb: 2 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          mb: 1,
                        }}
                      >
                        <Typography variant="subtitle2" fontWeight={600}>
                          KPI's
                        </Typography>
                        <IconButton size="small" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                          <MoreHoriz fontSize="small" />
                        </IconButton>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <KPIBox>
                          <Typography variant="h6" fontWeight={700}>
                            {campaignPreview.kpis.attention}
                          </Typography>
                          <Typography variant="caption" sx={{ opacity: 0.8 }}>
                            Attention
                          </Typography>
                        </KPIBox>
                        <KPIBox>
                          <Typography variant="h6" fontWeight={700}>
                            {campaignPreview.kpis.viewability}
                          </Typography>
                          <Typography variant="caption" sx={{ opacity: 0.8 }}>
                            Viewability
                          </Typography>
                        </KPIBox>
                        <KPIBox>
                          <Typography variant="h6" fontWeight={700}>
                            {campaignPreview.kpis.reach}
                          </Typography>
                          <Typography variant="caption" sx={{ opacity: 0.8 }}>
                            Reach
                          </Typography>
                        </KPIBox>
                      </Box>
                    </Box>

                    {/* Audience Section */}
                    <Box sx={{ px: 2, pb: 2 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          mb: 1,
                        }}
                      >
                        <Typography variant="subtitle2" fontWeight={600}>
                          Audience
                        </Typography>
                        <IconButton size="small" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                          <MoreHoriz fontSize="small" />
                        </IconButton>
                      </Box>
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        Gender: {campaignPreview.audience.gender}
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        Age: {campaignPreview.audience.age}
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        HHI: {campaignPreview.audience.hhi}
                      </Typography>
                    </Box>
                  </>
                )
              )}
            </CardContent>
          </MediaPlanningCard>
        </Grid>

        {/* Column 2: Analytics Activity Feed */}
        <Grid size={{ xs: 12, md: 3 }}>
          <SectionCard>
            <CardContent sx={{ p: 0, height: '100%', display: 'flex', flexDirection: 'column' }}>
              {/* Header */}
              <Box sx={{ p: 2, pb: 1 }}>
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 600, fontSize: '1.25rem', color: 'text.primary' }}
                >
                  Analytics
                </Typography>
              </Box>

              {loading ? (
                <Box sx={{ p: 2, flex: 1 }}>
                  {[1, 2, 3, 4].map((i) => (
                    <Skeleton
                      key={i}
                      variant="rectangular"
                      height={56}
                      sx={{ mb: 1, borderRadius: 1 }}
                    />
                  ))}
                </Box>
              ) : (
                <>
                  <List disablePadding sx={{ flex: 1 }}>
                    {activityItems.map((item, index) => (
                      <React.Fragment key={item.id}>
                        <ActivityListItem>
                          <ListItemAvatar>
                            <Avatar sx={{ bgcolor: brandColors.brightTeal, width: 40, height: 40 }}>
                              <Typography variant="body2" fontWeight={700}>
                                A
                              </Typography>
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={
                              <Typography variant="body2" fontWeight={500}>
                                {item.brandName}: {item.campaignName}
                              </Typography>
                            }
                            secondary={
                              <Typography variant="caption" color="text.secondary">
                                {item.dates}
                              </Typography>
                            }
                          />
                        </ActivityListItem>
                        {index < activityItems.length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </List>
                  <Box
                    sx={{
                      p: 2,
                      pt: 1,
                      textAlign: 'center',
                      borderTop: `1px solid ${brandColors.paleGray}`,
                    }}
                  >
                    <Link
                      href="#"
                      underline="none"
                      sx={{
                        color: 'text.secondary',
                        fontWeight: 500,
                        fontSize: '0.875rem',
                        '&:hover': { color: brandColors.teal },
                      }}
                    >
                      All Activity
                    </Link>
                  </Box>
                </>
              )}
            </CardContent>
          </SectionCard>
        </Grid>

        {/* Column 3: Recent Items (separate module) */}
        <Grid size={{ xs: 12, md: 3.5 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, height: '100%' }}>
            {/* Recent Items Module */}
            <SectionCard sx={{ flex: 2 }}>
              <CardContent sx={{ p: 0, height: '100%', display: 'flex', flexDirection: 'column' }}>
                {/* Header */}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    p: 2,
                    pb: 1,
                  }}
                >
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 600, fontSize: '1.25rem', color: 'text.primary' }}
                  >
                    Recent Items
                  </Typography>
                  <IconButton size="small">
                    <MoreHoriz />
                  </IconButton>
                </Box>

                {loading ? (
                  <Box sx={{ p: 2, flex: 1 }}>
                    {[1, 2, 3].map((i) => (
                      <Skeleton
                        key={i}
                        variant="rectangular"
                        height={70}
                        sx={{ mb: 1, borderRadius: 1 }}
                      />
                    ))}
                  </Box>
                ) : (
                  <List disablePadding sx={{ flex: 1 }}>
                    {recentItems.map((item, index) => (
                      <React.Fragment key={item.id}>
                        <ListItem sx={{ px: 2, py: 1.5 }}>
                          <ListItemAvatar>
                            <Avatar
                              sx={{
                                width: 48,
                                height: 48,
                                bgcolor:
                                  index === 0
                                    ? brandColors.teal
                                    : index === 1
                                      ? '#FF6B35'
                                      : '#4CAF50',
                                fontSize: '0.7rem',
                                fontWeight: 700,
                              }}
                            >
                              {index === 0 ? 'on' : index === 1 ? 'AWAY' : 'G'}
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primaryTypographyProps={{ component: 'div' }}
                            secondaryTypographyProps={{ component: 'div' }}
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body2" fontWeight={600}>
                                  {item.title}
                                </Typography>
                                <Chip
                                  label={item.status}
                                  size="small"
                                  color={getStatusColor(item.status)}
                                  sx={{ fontSize: '0.65rem', height: 20 }}
                                />
                              </Box>
                            }
                            secondary={
                              <Box>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  display="block"
                                >
                                  {item.budget}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  display="block"
                                >
                                  {item.dates}
                                </Typography>
                                <Typography variant="caption" color="text.disabled">
                                  ⏱ {item.timestamp}
                                </Typography>
                              </Box>
                            }
                          />
                        </ListItem>
                        {index < recentItems.length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </List>
                )}
              </CardContent>
            </SectionCard>

            {/* Quick Links Module */}
            <SectionCard sx={{ flex: 1 }}>
              <CardContent sx={{ p: 0, height: '100%', display: 'flex', flexDirection: 'column' }}>
                {/* Header */}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    p: 2,
                    pb: 1,
                  }}
                >
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 600, fontSize: '1.25rem', color: 'text.primary' }}
                  >
                    Quick Links
                  </Typography>
                  <IconButton size="small">
                    <MoreHoriz />
                  </IconButton>
                </Box>

                <Box
                  sx={{ p: 2, pt: 0, flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}
                >
                  {hasModule(ModuleKey.PLANNING) && (
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={() => navigate('/planning')}
                      sx={{
                        bgcolor: brandColors.teal,
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 600,
                        '&:hover': { bgcolor: brandColors.deepTeal },
                      }}
                    >
                      All Activity
                    </Button>
                  )}
                  {hasModule(ModuleKey.ADMIN_METADATA_MANAGER) && (
                    <Button
                      variant="outlined"
                      fullWidth
                      onClick={() => navigate('/admin/metadata-manager')}
                      sx={{
                        color: brandColors.teal,
                        borderColor: brandColors.teal,
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 600,
                        '&:hover': {
                          bgcolor: brandColors.offWhite,
                          borderColor: brandColors.deepTeal,
                        },
                      }}
                    >
                      Metadata Manager
                    </Button>
                  )}
                </Box>
              </CardContent>
            </SectionCard>
          </Box>
        </Grid>

        {/* Column 4: Quick Actions */}
        <Grid size={{ xs: 12, md: 2.5 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, height: '100%' }}>
            {/* Create a Media Plan */}
            {can(PermissionKey.MEDIAPLAN_CREATE) && (
              <QuickActionCard onClick={() => navigate('/planning/create')} sx={{ flex: 1 }}>
                <Box>
                  <Typography
                    variant="h4"
                    fontWeight={600}
                    sx={{
                      color: brandColors.darkCharcoal,
                      lineHeight: 1.3,
                    }}
                  >
                    Create a<br />
                    Media Plan
                  </Typography>
                </Box>
                <Box sx={{ alignSelf: 'flex-end' }}>
                  <IconButton
                    sx={{
                      bgcolor: brandColors.offWhite,
                      border: `1px solid ${brandColors.lightGray}`,
                      '&:hover': { bgcolor: brandColors.paleGray },
                    }}
                  >
                    <ArrowForward />
                  </IconButton>
                </Box>
              </QuickActionCard>
            )}

            {/* Review Performance */}
            {hasModule(ModuleKey.ANALYTICS) && (
              <QuickActionCard onClick={() => navigate('/analytics')} sx={{ flex: 1 }}>
                <Box>
                  <Typography
                    variant="h4"
                    fontWeight={600}
                    sx={{
                      color: brandColors.darkCharcoal,
                      lineHeight: 1.3,
                    }}
                  >
                    Review
                    <br />
                    Performance
                  </Typography>
                </Box>
                <Box sx={{ alignSelf: 'flex-end' }}>
                  <IconButton
                    sx={{
                      bgcolor: brandColors.offWhite,
                      border: `1px solid ${brandColors.lightGray}`,
                      '&:hover': { bgcolor: brandColors.paleGray },
                    }}
                  >
                    <Assessment />
                  </IconButton>
                </Box>
              </QuickActionCard>
            )}
          </Box>
        </Grid>
      </Grid>
    </SkeletonPage>
  );
};

export default HomePage;
